/**
 * client/src/pages/Offline.tsx — what this browser has, when the network does not.
 *
 * Three jobs, and they are one screen because a person with no signal asks one question:
 *
 *  1. **The offline shelf.** Which books are actually here, how big they are, and how much of each
 *     has a voice. Built from the download manifest in IndexedDB, never from `GET /shelf` — the
 *     shelf is a per-actor answer on a device several people may share, and the point of this screen
 *     is that it is right with the radio off.
 *  2. **The outbox.** What you wrote while you were offline and where it has got to. §5.8 promises
 *     positions and "Sounds wrong" reports survive a tunnel; a promise nobody can see is a promise
 *     nobody believes.
 *  3. **An honest state.** Whether the app shell is installed, whether a new version is waiting, and
 *     — the whole reason this route exists — what a reader is looking at instead of a blank page.
 *
 * ⚠️ **Not a blank page anywhere.** The failure this screen is written against is the one every
 * offline-capable app ships: the network dies, five screens render `null` while a fetch hangs, and
 * the product looks broken rather than offline. `<OfflineNotice>` is exported for exactly that, and
 * the route is reachable from it, so "I have no signal" always has somewhere to go.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppFrame, BookCover, IconAlert, formatBytes, timeAgo } from "../App.tsx";
import { A2HSInvite, OfflineStyles } from "../components/DownloadBook.tsx";
import { evictOrphanAudio, listOfflineBooks, removeOfflineBook, type OfflineBook } from "../lib/idb.ts";
import {
  applyUpdate,
  flushOutbox,
  isOffline,
  pending,
  serviceWorkerState,
  subscribeOffline,
  subscribeServiceWorker,
  type ServiceWorkerState,
} from "../lib/outbox.ts";

// ─── the hooks other screens use ─────────────────────────────────────────────

/**
 * The books this browser is holding, and a `has()` for filtering a shelf by them.
 *
 * Exported because `pages/Shelf.tsx` renders the **offline filter** with it: the filter is a
 * property of local storage, not of the server's answer, so it cannot come from `/shelf` and it must
 * not be re-derived. One reader, one place.
 */
export function useOfflineBooks(): {
  books: OfflineBook[];
  ids: ReadonlySet<string>;
  loading: boolean;
  refresh: () => void;
} {
  const [books, setBooks] = useState<OfflineBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    void listOfflineBooks().then((rows) => {
      if (!live) return;
      setBooks(rows);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [nonce]);

  return {
    books,
    ids: new Set(books.map((b) => b.bookId)),
    loading,
    refresh: useCallback(() => setNonce((n) => n + 1), []),
  };
}

/** `true` when the OS says there is no route, or the last real request never reached the server. */
export function useOffline(): boolean {
  const [off, setOff] = useState(isOffline);
  useEffect(() => subscribeOffline(() => setOff(isOffline())), []);
  return off;
}

function useServiceWorker(): ServiceWorkerState {
  const [state, setState] = useState(serviceWorkerState);
  useEffect(() => subscribeServiceWorker(() => setState(serviceWorkerState())), []);
  return state;
}

/**
 * The banner a screen shows instead of rendering nothing.
 *
 * Deliberately not a takeover: a shelf painted from `useCachedQuery`'s IndexedDB copy is still a
 * useful shelf, and blanking it because a refresh failed is how an app reads as broken on a train
 * (`Shelf.tsx` already makes the same argument about its error row). This says what is true and
 * points at the one screen that works with no network at all.
 */
export function OfflineNotice({ what }: { what?: string }) {
  const off = useOffline();
  if (!off) return null;
  return (
    <p className="ab-note ab-note-warn">
      <OfflineStyles />
      <IconAlert />
      <span>
        You&rsquo;re offline{what ? ` — ${what}` : ""}.{" "}
        <Link to="/offline" className="ab-link">
          See what&rsquo;s on this device
        </Link>
        .
      </span>
    </p>
  );
}

// ─── the screen ──────────────────────────────────────────────────────────────

export function Offline() {
  const { books, loading, refresh } = useOfflineBooks();
  const off = useOffline();
  const sw = useServiceWorker();
  const [queued, setQueued] = useState<{ key: string; at: number; item: { label?: string; attempts?: number } }[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [swept, setSwept] = useState<number | null>(null);
  const [free, setFree] = useState<{ usage: number; quota: number } | null>(null);

  const reloadQueue = useCallback(() => {
    void pending().then(setQueued);
  }, []);
  useEffect(reloadQueue, [reloadQueue]);

  useEffect(() => {
    void navigator.storage
      ?.estimate?.()
      .then((e) => {
        if (typeof e.quota === "number") setFree({ usage: e.usage ?? 0, quota: e.quota });
      })
      .catch(() => setFree(null));
  }, [books]);

  const total = books.reduce((n, b) => n + b.bytes, 0);

  return (
    <AppFrame>
      <OfflineStyles />
      <h1 className="ab-h1">On this device</h1>
      <p className="ab-lede">
        {off
          ? "You're offline. These books are here in full — the text and the voice — and everything below works with the radio off."
          : "Books you've downloaded, and anything waiting to be sent."}
      </p>

      {/* The state of the app shell itself. A reader who has never been here with a connection has no
          precached shell, and "offline reading" would silently mean "a blank page" — say so. */}
      <ShellState sw={sw} />

      <div className="ab-offline-bar">
        <strong>{books.length === 1 ? "1 book" : `${books.length} books`}</strong>
        <span className="ab-chip ab-chip-quiet">{formatBytes(total)}</span>
        {free && (
          <span className="ab-chip ab-chip-quiet">
            {formatBytes(Math.max(0, free.quota - free.usage))} free
          </span>
        )}
      </div>

      {loading ? (
        <p className="ab-hint">Looking…</p>
      ) : books.length === 0 ? (
        <div className="ab-empty">
          <h2>Nothing is downloaded yet</h2>
          <p>
            Open a book and choose <em>Make this book available offline</em>. It tells you how many
            megabytes it is before it starts.
          </p>
          <Link to="/" className="ab-btn ab-btn-primary">
            Back to the shelf
          </Link>
        </div>
      ) : (
        <div className="ab-offline-grid">
          {books.map((b) => (
            <div className="ab-offline-row" key={b.bookId}>
              <BookCover bookId={b.bookId} title={b.title} author={b.author} coverSha={b.coverSha} />
              <div className="ab-offline-row-body">
                <strong>
                  <Link to={`/b/${b.bookId}/read`}>{b.title}</Link>
                </strong>
                <span>
                  {formatBytes(b.bytes)} · {b.pages.length} of {b.pagesTotal} pages have a voice ·
                  downloaded {timeAgo(b.at)}
                </span>
              </div>
              <button
                type="button"
                className="ab-btn ab-btn-ghost ab-btn-sm"
                disabled={busy === b.bookId}
                onClick={() => {
                  setBusy(b.bookId);
                  void removeOfflineBook(b.bookId).finally(() => {
                    setBusy(null);
                    refresh();
                  });
                }}
              >
                {busy === b.bookId ? "Removing…" : "Remove"}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* §5.8's two limits, stated rather than hidden. Both are true on the device this product is
          mainly used on, and neither is discoverable by looking at the app. */}
      <section className="ab-panel">
        <h2 className="ab-h2">Two things worth knowing</h2>
        <p>
          <strong>Safari clears a website&rsquo;s stored files after seven days without a visit.</strong>{" "}
          That is why nothing here says &ldquo;downloaded&rdquo; — it says <em>available offline
          (recently)</em>, because that is all any website can honestly promise. A site on your home
          screen is exempt.
        </p>
        <p>
          <strong>A home-screen app has its own storage and its own cookie jar.</strong> Downloading a
          book in Safari and then installing gives you an app with none of it — and, if you are reading
          without an account, a different reader with a different place in the book.
        </p>
        <A2HSInvite reason="guest" />
      </section>

      <Outbox rows={queued} onFlush={reloadQueue} offline={off} />

      <section className="ab-panel">
        <h2 className="ab-h2">Housekeeping</h2>
        <p className="ab-hint">
          Audio left behind by a download that was stopped halfway belongs to no book and is never
          played. This finds it and gives the space back.
        </p>
        <button
          type="button"
          className="ab-btn ab-btn-ghost ab-btn-sm"
          onClick={() => {
            setSwept(null);
            void evictOrphanAudio().then((r) => setSwept(r.deleted));
          }}
        >
          Reclaim unused audio
        </button>
        {swept !== null && (
          <p className="ab-hint">
            {swept === 0 ? "Nothing to reclaim — every stored page belongs to a book." : `Removed ${swept} stray page${swept === 1 ? "" : "s"}.`}
          </p>
        )}
      </section>
    </AppFrame>
  );
}

// ─── the shell ───────────────────────────────────────────────────────────────

function ShellState({ sw }: { sw: ServiceWorkerState }) {
  if (!sw.supported) {
    return (
      <p className="ab-note ab-note-warn">
        <IconAlert />
        <span>
          This browser won&rsquo;t keep the app itself offline, so opening the site with no connection
          will show the browser&rsquo;s own error page. Books you have downloaded are still on the
          device and play as soon as the app opens.
        </span>
      </p>
    );
  }
  if (sw.updateReady) {
    return (
      <p className="ab-note">
        <span>
          A new version of the app is ready. It waits on purpose — swapping it in under an open book
          would take away the files that book is still loading.{" "}
          <button type="button" className="ab-link" onClick={() => void applyUpdate()}>
            Use it now
          </button>
        </span>
      </p>
    );
  }
  if (!sw.controlling) {
    return (
      <p className="ab-hint">
        The app is being stored for offline use. It is ready from the next time you open the site.
      </p>
    );
  }
  return <p className="ab-hint ab-hint-quiet">The app itself is stored on this device{sw.build ? ` (build ${sw.build})` : ""}.</p>;
}

// ─── the outbox ──────────────────────────────────────────────────────────────

function Outbox({
  rows,
  onFlush,
  offline,
}: {
  rows: { key: string; at: number; item: { label?: string; attempts?: number } }[];
  onFlush: () => void;
  offline: boolean;
}) {
  const [sending, setSending] = useState(false);

  if (rows.length === 0) {
    return (
      <section className="ab-panel">
        <h2 className="ab-h2">Waiting to be sent</h2>
        <p className="ab-hint">
          Nothing. Where you are in each book, and anything you flagged as sounding wrong, is with the
          server.
        </p>
      </section>
    );
  }

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">Waiting to be sent</h2>
      <p className="ab-hint">
        {offline
          ? "These go out on their own the moment you have a connection. Nothing is lost by closing the app."
          : "These are being sent now."}
      </p>
      <ul className="ab-facts">
        {rows.map((r) => (
          <li key={r.key}>
            {r.item.label ?? r.key} <span className="ab-hint ab-hint-quiet">· {timeAgo(r.at)}</span>
            {(r.item.attempts ?? 0) > 0 && (
              <span className="ab-hint ab-hint-quiet"> · {r.item.attempts} attempts</span>
            )}
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="ab-btn ab-btn-ghost ab-btn-sm"
        disabled={sending || offline}
        onClick={() => {
          setSending(true);
          void flushOutbox().finally(() => {
            setSending(false);
            onFlush();
          });
        }}
      >
        {sending ? "Sending…" : "Send now"}
      </button>
    </section>
  );
}


